/**
 * DocumentView - PDF viewer with interactive block overlays
 * Based on working psagot_v3/static/index.html implementation
 * 
 * Features:
 * - PDF.js renders the actual PDF
 * - Block overlays positioned on top
 * - Toolbar appears on hover (120ms delay)
 * - Copy text, Export Excel, Copy Image
 * - JSON inspector sidebar for debugging
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import * as XLSX from 'xlsx';

declare global {
  interface Window {
    pdfjsLib: any;
  }
}

interface Block {
  id: string;
  block_type: string;
  bbox: [number, number, number, number];
  text?: string;
  html?: string;
  children?: Block[];
  figure_caption?: string;
  caption?: string;  // Alternative caption field
  images?: Record<string, string>;  // Raw base64 by block ID
  image_url?: string;  // Full data URL (data:image/jpeg;base64,...)
  page?: number;
}

interface DocumentData {
  id: string;
  title: string;
  filename: string;
  source: string;
  storage_url?: string;
  layout_data?: Block[];
  report_date?: string;
  analyst_names?: string[];
  company_tickers?: string[];
}

const API_BASE = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://127.0.0.1:8000'
  : 'https://api.wallst.co.il';
const API_KEY = process.env.REACT_APP_API_KEY || 'psagot-api-key-2024-secure';

// Normalize bbox to array format [x, y, x2, y2]
function normalizeBbox(bbox: any): [number, number, number, number] | null {
  if (!bbox) return null;
  // Already array format
  if (Array.isArray(bbox) && bbox.length >= 4) {
    return [bbox[0], bbox[1], bbox[2], bbox[3]];
  }
  // Object format {x, y, width, height}
  if (typeof bbox === 'object' && 'x' in bbox && 'y' in bbox) {
    const x = bbox.x || 0;
    const y = bbox.y || 0;
    const width = bbox.width || 0;
    const height = bbox.height || 0;
    return [x, y, x + width, y + height];
  }
  return null;
}

// Normalize block_type to TitleCase (e.g., 'text' -> 'Text')
function normalizeBlockType(blockType: string): string {
  if (!blockType) return blockType;
  return blockType.charAt(0).toUpperCase() + blockType.slice(1).toLowerCase();
}

// Process a single block into the map
function processBlockToMap(node: any, map: Record<string, Block>): void {
  if (!node || typeof node !== 'object') return;
  const blockId = node.block_id || node.id;
  const blockType = normalizeBlockType(node.block_type);
  if (blockId && node.bbox && blockType !== 'Page') {
    const normalizedBbox = normalizeBbox(node.bbox);
    if (normalizedBbox) {
      // Extract page number from various sources
      let pageNum = node.page_number ?? node.page;
      if (pageNum === undefined && typeof node.bbox === 'object' && 'page' in node.bbox) {
        pageNum = node.bbox.page;
      }
      if (pageNum === undefined) {
        const pageMatch = blockId.match(/\/page\/(\d+)/);
        if (pageMatch) pageNum = parseInt(pageMatch[1], 10);
      }
      map[blockId] = { ...node, id: blockId, block_type: blockType, bbox: normalizedBbox, page: pageNum };
    }
  }
}

function flattenBlocks(layoutData: any, map: Record<string, Block> = {}): Record<string, Block> {
  if (!layoutData) return map;

  // Handle array input (recursive calls)
  if (Array.isArray(layoutData)) {
    layoutData.forEach(n => flattenBlocks(n, map));
    return map;
  }

  // If layout_data has a 'blocks' array, process it directly
  if (typeof layoutData === 'object' && 'blocks' in layoutData && Array.isArray(layoutData.blocks)) {
    layoutData.blocks.forEach((block: any) => processBlockToMap(block, map));
  }

  // Also traverse nested structure for older format
  if (typeof layoutData === 'object') {
    processBlockToMap(layoutData, map);
    if (layoutData.children) {
      flattenBlocks(layoutData.children, map);
    }
    // Recurse into pages if present
    if (layoutData.pages) {
      if (Array.isArray(layoutData.pages)) {
        layoutData.pages.forEach((page: any) => flattenBlocks(page, map));
      } else if (typeof layoutData.pages === 'object') {
        Object.values(layoutData.pages).forEach((page: any) => flattenBlocks(page, map));
      }
    }
  }

  return map;
}

// Check if a block will be rendered (not skipped due to having children)
function isBlockRenderable(block: Block): boolean {
  // Blocks with children are skipped during rendering (except Tables)
  if (block.children?.length && block.block_type !== 'Table') {
    return false;
  }
  // TableRow/TableCell are also skipped
  if (block.block_type === 'TableRow' || block.block_type === 'TableCell') {
    return false;
  }
  return true;
}

// Find a block to highlight - handles partial matches and parent/child relationships
function findBlockToHighlight(blocks: Record<string, Block>, highlightId: string | null): string | null {
  if (!highlightId || !blocks) return null;

  const blockIds = Object.keys(blocks);

  // Helper to find first renderable child from block's children property
  const findRenderableChild = (block: Block): string | null => {
    if (!block.children) return null;
    for (const child of block.children) {
      if (child.id && blocks[child.id] && isBlockRenderable(blocks[child.id])) {
        return child.id;
      }
    }
    return null;
  };

  // 1. Exact match - but only if the block is renderable
  if (blocks[highlightId]) {
    if (isBlockRenderable(blocks[highlightId])) {
      return highlightId;
    }
    // Block exists but won't be rendered - find a renderable child from its children property
    const childFromProp = findRenderableChild(blocks[highlightId]);
    if (childFromProp) return childFromProp;

    // Fallback: try ID prefix matching
    const renderableChild = blockIds.find(id =>
      id.startsWith(highlightId + '/') && isBlockRenderable(blocks[id])
    );
    if (renderableChild) return renderableChild;
  }

  // 2. URL-decoded match (in case of encoding issues)
  const decoded = decodeURIComponent(highlightId);
  if (blocks[decoded]) {
    if (isBlockRenderable(blocks[decoded])) {
      return decoded;
    }
    const childFromProp = findRenderableChild(blocks[decoded]);
    if (childFromProp) return childFromProp;

    const renderableChild = blockIds.find(id =>
      id.startsWith(decoded + '/') && isBlockRenderable(blocks[id])
    );
    if (renderableChild) return renderableChild;
  }

  // 3. Find blocks that start with this id (children of the requested block)
  const childMatch = blockIds.find(id => id.startsWith(highlightId + '/') && isBlockRenderable(blocks[id]));
  if (childMatch) return childMatch;

  // 4. Find blocks where highlightId is a parent path (and renderable)
  const parentMatch = blockIds.find(id => highlightId.startsWith(id) && isBlockRenderable(blocks[id]));
  if (parentMatch) return parentMatch;

  // 5. Extract page and index from highlight ID to find nearby blocks
  // Format: /page/0/ListGroup/481 -> page=0, type=ListGroup, index=481
  const idMatch = highlightId.match(/\/page\/(\d+)\/(\w+)\/(\d+)/);
  if (idMatch) {
    const [, pageNum, blockType, indexStr] = idMatch;
    const targetIndex = parseInt(indexStr);
    const pagePrefix = `/page/${pageNum}`;

    // Get all blocks on the same page
    const samePageBlocks = blockIds.filter(id => id.startsWith(pagePrefix));

    // If looking for ListGroup, try to find ListItem with similar index
    if (blockType === 'ListGroup') {
      const listItems = samePageBlocks.filter(id => id.includes('/ListItem/'));
      // Find ListItem with closest index
      let closestMatch: string | null = null;
      let closestDiff = Infinity;
      for (const id of listItems) {
        const itemMatch = id.match(/\/ListItem\/(\d+)/);
        if (itemMatch) {
          const itemIndex = parseInt(itemMatch[1]);
          const diff = Math.abs(itemIndex - targetIndex);
          if (diff < closestDiff) {
            closestDiff = diff;
            closestMatch = id;
          }
        }
      }
      if (closestMatch && closestDiff < 10) return closestMatch;
    }

    // Try to find same type with closest index
    const sameTypeBlocks = samePageBlocks.filter(id => id.includes(`/${blockType}/`));
    if (sameTypeBlocks.length > 0) {
      let closestMatch = sameTypeBlocks[0];
      let closestDiff = Infinity;
      for (const id of sameTypeBlocks) {
        const match = id.match(new RegExp(`\\/${blockType}\\/(\\d+)`));
        if (match) {
          const blockIndex = parseInt(match[1]);
          const diff = Math.abs(blockIndex - targetIndex);
          if (diff < closestDiff) {
            closestDiff = diff;
            closestMatch = id;
          }
        }
      }
      return closestMatch;
    }

    // Fallback: any block on same page
    if (samePageBlocks.length > 0) return samePageBlocks[0];
  }

  // 6. Last resort: just try page matching
  const pageMatch = highlightId.match(/\/page\/(\d+)/);
  if (pageMatch) {
    const pagePrefix = `/page/${pageMatch[1]}`;
    const samePageBlocks = blockIds.filter(id => id.startsWith(pagePrefix));
    if (samePageBlocks.length > 0) return samePageBlocks[0];
  }

  return null;
}

function cleanText(str: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = str.replace(/<br\s*\/?>/gi, ' ').replace(/\n/g, ' ');
  return (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
}

const DocumentView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const docId = searchParams.get('id') || searchParams.get('docId');
  const highlightBlockIdParam = searchParams.get('highlightBlock');

  const [document, setDocument] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<Record<string, Block>>({});
  const [resolvedHighlightId, setResolvedHighlightId] = useState<string | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);
  const [hoveredBlock, setHoveredBlock] = useState<Block | null>(null);
  const [toolbarPos, setToolbarPos] = useState<{ top: number; left: number } | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const showTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Resolve the highlight block ID once blocks are loaded
  useEffect(() => {
    if (blocks && Object.keys(blocks).length > 0 && highlightBlockIdParam) {
      const resolved = findBlockToHighlight(blocks, highlightBlockIdParam);
      console.log('Highlight resolution:', highlightBlockIdParam, '->', resolved);
      setResolvedHighlightId(resolved);
    }
  }, [blocks, highlightBlockIdParam]);

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch document
  useEffect(() => {
    if (!docId) return;

    const fetchDocument = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/api/v3/documents/${docId}`, {
          headers: { 'X-API-Key': API_KEY },
        });
        if (!response.ok) throw new Error('Document not found');
        const data = await response.json();
        setDocument(data);
        
        if (data.layout_data) {
          const flatBlocks = flattenBlocks(data.layout_data);
          console.log('Loaded blocks:', Object.keys(flatBlocks).length);
          setBlocks(flatBlocks);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [docId]);

  // Track current render to cancel stale renders
  const renderIdRef = useRef(0);

  // Render PDF pages with overlays
  useEffect(() => {
    if (!document?.storage_url || !window.pdfjsLib || !containerRef.current) return;

    // Increment render ID to cancel any in-progress renders
    const currentRenderId = ++renderIdRef.current;

    const renderPDF = async () => {
      const container = containerRef.current!;
      container.innerHTML = '';

      const pdf = await window.pdfjsLib.getDocument(document.storage_url).promise;

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        // Check if this render was superseded by a newer one
        if (renderIdRef.current !== currentRenderId) {
          console.log(`Render ${currentRenderId} aborted (superseded by ${renderIdRef.current})`);
          return;
        }

        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.5 });
        const baseViewport = page.getViewport({ scale: 1.0 });
        const scaleX = viewport.width / baseViewport.width;
        const scaleY = viewport.height / baseViewport.height;

        // Page container
        const pageDiv = window.document.createElement('div');
        pageDiv.className = 'pdf-page';
        pageDiv.style.cssText = `position:relative;width:${viewport.width}px;height:${viewport.height}px;margin:0 auto 30px;box-shadow:0 8px 20px rgba(0,0,0,0.4);background:white;`;

        // Canvas for PDF
        const canvas = window.document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext('2d')!, viewport }).promise;
        pageDiv.appendChild(canvas);

        // Overlay layer for blocks
        const layer = window.document.createElement('div');
        layer.className = 'chunk-layer';
        layer.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;';

        Object.values(blocks).forEach(block => {
          // Extract page index from block id like "/page/0/Text/1" (0-indexed from backend)
          const pageMatch = block.id.match(/\/page\/(\d+)/);
          const blockPageIdx = pageMatch ? parseInt(pageMatch[1]) : -1;
          // Block IDs are 0-indexed, pageNum is 1-indexed, so compare with pageNum - 1
          if (blockPageIdx !== pageNum - 1) return;

          // Skip parent blocks with children (except Tables)
          if (block.children?.length && block.block_type !== 'Table') return;
          
          // Skip TableRow/TableCell - interact with Table parent only
          if (block.block_type === 'TableRow' || block.block_type === 'TableCell') return;

          const [x, y, x2, y2] = block.bbox;
          const el = window.document.createElement('div');
          el.className = 'block';
          el.id = block.id;
          el.dataset.type = block.block_type;
          el.style.cssText = `position:absolute;left:${x*scaleX}px;top:${y*scaleY}px;width:${(x2-x)*scaleX}px;height:${(y2-y)*scaleY}px;pointer-events:auto;cursor:pointer;border:1px solid transparent;transition:all 0.1s;`;

          // Style by type
          if (block.block_type === 'Table') {
            el.style.border = '1px dashed rgba(255,152,0,0.4)';
          } else if (block.block_type === 'Figure' || block.block_type === 'Picture') {
            el.style.border = '1px dashed rgba(156,39,176,0.4)';
          }

          // Highlight if selected (use resolved ID for better matching)
          if (block.id === resolvedHighlightId) {
            el.style.background = 'rgba(255,235,59,0.3)';
            el.style.border = '2px solid #fdd835';
            setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 500);
          }

          el.onmouseenter = () => handleBlockMouseEnter(block, el);
          el.onmouseleave = () => handleBlockMouseLeave();
          el.onclick = () => setSelectedBlock(block);

          layer.appendChild(el);
        });

        pageDiv.appendChild(layer);

        // Final check before appending - abort if superseded
        if (renderIdRef.current !== currentRenderId) {
          return;
        }
        container.appendChild(pageDiv);
      }
    };

    renderPDF();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, blocks, resolvedHighlightId]);

  // Toolbar hover handlers - increased delays for easier interaction
  const handleBlockMouseEnter = useCallback((block: Block, el: HTMLElement) => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    showTimerRef.current = setTimeout(() => {
      const rect = el.getBoundingClientRect();
      // Position toolbar inside the block (top-right corner) for easier access
      setToolbarPos({ top: rect.top + 5, left: rect.right - 120 });
      setHoveredBlock(block);
    }, 80);  // Faster show
  }, []);

  const handleBlockMouseLeave = useCallback(() => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setHoveredBlock(null);
      setToolbarPos(null);
    }, 500);  // Longer delay to reach toolbar
  }, []);

  const handleToolbarMouseEnter = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  const handleToolbarMouseLeave = useCallback(() => {
    hideTimerRef.current = setTimeout(() => {
      setHoveredBlock(null);
      setToolbarPos(null);
    }, 300);
  }, []);

  // Copy text
  const handleCopyText = useCallback(() => {
    if (!hoveredBlock) return;
    // Check multiple caption fields, then fall back to html/text
    const text = hoveredBlock.figure_caption || hoveredBlock.caption || cleanText(hoveredBlock.html || hoveredBlock.text || '');
    navigator.clipboard.writeText(text);
  }, [hoveredBlock]);

  // Copy image
  const handleCopyImage = useCallback(async () => {
    if (!hoveredBlock) return;

    // Try image_url first (full data URL), then images dict (raw base64)
    let imageDataUrl = hoveredBlock.image_url;
    if (!imageDataUrl && hoveredBlock.images) {
      const rawBase64 = hoveredBlock.images[hoveredBlock.id];
      if (rawBase64) {
        // Detect image type from base64 header
        const isJpeg = rawBase64.startsWith('/9j/');
        const isPng = rawBase64.startsWith('iVBOR');
        const mimeType = isJpeg ? 'image/jpeg' : isPng ? 'image/png' : 'image/jpeg';
        imageDataUrl = `data:${mimeType};base64,${rawBase64}`;
      }
    }

    if (!imageDataUrl) {
      alert('No image data available');
      return;
    }

    try {
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      // Convert to PNG for clipboard (better compatibility)
      const pngBlob = await convertToPng(blob);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
    } catch (err) {
      console.error('Copy image error:', err);
      alert('Failed to copy image');
    }
  }, [hoveredBlock]);

  // Helper to convert image blob to PNG for clipboard
  const convertToPng = async (blob: Blob): Promise<Blob> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = window.document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((pngBlob) => resolve(pngBlob!), 'image/png');
      };
      img.src = URL.createObjectURL(blob);
    });
  };

  // Export Excel
  const handleExportExcel = useCallback(() => {
    if (!hoveredBlock) return;
    
    let rows: string[][] = [];

    // Method 1: TableRow children
    if (hoveredBlock.children) {
      const tableRows = hoveredBlock.children.filter(c => c.block_type === 'TableRow');
      if (tableRows.length) {
        rows = tableRows.map(row => 
          (row.children || []).map(cell => cleanText(cell.text || cell.html || ''))
        );
      }
    }

    // Method 2: Group flat cells by Y
    if (!rows.length && hoveredBlock.children) {
      const cells = hoveredBlock.children.filter(c => c.block_type === 'TableCell');
      if (cells.length) {
        const byY: Record<number, Block[]> = {};
        cells.forEach(c => {
          const y = Math.round(c.bbox[1]);
          (byY[y] = byY[y] || []).push(c);
        });
        rows = Object.keys(byY)
          .sort((a, b) => Number(a) - Number(b))
          .map(y => byY[Number(y)]
            .sort((a, b) => a.bbox[0] - b.bbox[0])
            .map(c => cleanText(c.text || c.html || ''))
          );
      }
    }

    if (!rows.length) {
      alert('Could not extract table');
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    
    // Auto-size columns
    const widths: number[] = [];
    rows.forEach(row => row.forEach((cell, i) => {
      widths[i] = Math.max(widths[i] || 8, Math.min(String(cell).length + 2, 40));
    }));
    ws['!cols'] = widths.map(w => ({ wch: w }));
    
    XLSX.utils.book_append_sheet(wb, ws, 'Table');
    
    const parts = hoveredBlock.id.split('/');
    XLSX.writeFile(wb, `Page${Number(parts[2]) + 1}_Table${Number(parts[4]) + 1}.xlsx`);
  }, [hoveredBlock]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#1e1e1e' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const isTable = hoveredBlock?.block_type === 'Table';
  const isImage = hoveredBlock?.block_type === 'Figure' || hoveredBlock?.block_type === 'Picture';

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#1e1e1e' }}>
      {/* Mobile toggle button for sidebar */}
      {isMobile && (
        <Box
          onClick={() => setSidebarOpen(!sidebarOpen)}
          sx={{
            position: 'fixed',
            top: 10,
            left: 10,
            zIndex: 1000,
            bgcolor: '#fff',
            p: 1,
            borderRadius: 1,
            boxShadow: 2,
            cursor: 'pointer'
          }}
        >
          {sidebarOpen ? '✕' : '☰'}
        </Box>
      )}

      {/* Sidebar - JSON Inspector (hidden on mobile unless toggled) */}
      <Box sx={{ 
        width: isMobile ? '80vw' : 350, 
        bgcolor: '#fff', 
        borderRight: '1px solid #ddd', 
        display: isMobile && !sidebarOpen ? 'none' : 'flex', 
        flexDirection: 'column',
        position: isMobile ? 'fixed' : 'relative',
        left: 0,
        top: 0,
        height: '100vh',
        zIndex: 999
      }}>
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #ddd', fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
          <span>Block Inspector</span>
          {isMobile && <span onClick={() => setSidebarOpen(false)} style={{ cursor: 'pointer' }}>✕</span>}
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {document && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>{document.title}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {document.company_tickers?.map(t => <Chip key={t} label={t} size="small" />)}
              </Box>
            </Box>
          )}
          <Box
            sx={{
              bgcolor: '#2d2d2d',
              color: '#76ff03',
              p: 1.5,
              borderRadius: 1,
              fontFamily: 'Consolas, monospace',
              fontSize: 11,
              whiteSpace: 'pre-wrap',
              minHeight: 150,
              overflow: 'auto'
            }}
          >
            {selectedBlock
              ? JSON.stringify(selectedBlock, null, 2)
              : 'Click on a block to inspect...'}
          </Box>
        </Box>
      </Box>

      {/* Main PDF area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 4, bgcolor: '#525659' }}>
        <div ref={containerRef} id="pdf-wrapper" />
      </Box>

      {/* Toolbar - appears on hover */}
      {hoveredBlock && toolbarPos && (
        <Box
          onMouseEnter={handleToolbarMouseEnter}
          onMouseLeave={handleToolbarMouseLeave}
          sx={{
            position: 'fixed',
            top: toolbarPos.top,
            left: toolbarPos.left,
            bgcolor: '#333',
            color: '#fff',
            p: '6px 10px',
            borderRadius: 1,
            fontSize: 12,
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            display: 'flex',
            gap: 1
          }}
        >
          {isTable ? (
            <button
              onClick={handleExportExcel}
              style={{
                background: 'none',
                border: 'none',
                color: '#4caf50',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 5
              }}
            >
              📊 Excel
            </button>
          ) : isImage ? (
            <>
              <button
                onClick={handleCopyImage}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#eee',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: 3
                }}
              >
                🖼️ Copy Image
              </button>
              {(hoveredBlock.figure_caption || hoveredBlock.caption) && (
                <button
                  onClick={handleCopyText}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#eee',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 3
                  }}
                >
                  📋 Caption
                </button>
              )}
            </>
          ) : (
            <button
              onClick={handleCopyText}
              style={{
                background: 'none',
                border: 'none',
                color: '#eee',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: 3
              }}
            >
              📋 Copy
            </button>
          )}
        </Box>
      )}

      {/* Styles for block hover effects */}
      <style>{`
        .block:hover {
          background: rgba(0,102,204,0.1) !important;
          border: 1px solid #0066cc !important;
        }
        .block[data-type="Table"]:hover {
          background: rgba(255,152,0,0.15) !important;
          border: 2px solid #ff9800 !important;
        }
        .block[data-type="Figure"]:hover, .block[data-type="Picture"]:hover {
          background: rgba(156,39,176,0.15) !important;
          border: 2px solid #9c27b0 !important;
        }
      `}</style>
    </Box>
  );
};

export default DocumentView;
